/**
 * User-added Placement edges per project — persisted to localStorage.
 *
 * The sample / procgen project graph in data/sampleGraph.ts is read-only.
 * When a designer adds a SKU into a Room from the Catalog rail, we don't
 * mutate that source data; instead we record a typed IN_ROOM edge here
 * and merge into the graph at render time in Design.tsx.
 *
 * Per-project key — switching projects swaps the placement set.
 */
import type { GraphEdge, ProjectGraph } from "../types/ontology";

const KEY_PREFIX = "foyrmind.userPlacements.";
const UNDO_PREFIX = "foyrmind.userPlacements.undo.";
const REDO_PREFIX = "foyrmind.userPlacements.redo.";
/** Per-(project,sku,room) position override: x/z metres relative to the
 *  room's origin (the placeSkusInRoom anchor system). When absent, the
 *  category anchor (south/north/east/west/center) drives placement. */
const POSITION_KEY_PREFIX = "foyrmind.placementPositions.";

export interface UserPlacement {
  /** Stable edge id so React keys stay stable across renders. */
  readonly id: string;
  readonly skuId: string;
  readonly roomId: string;
  readonly createdAt: string;
}

function key(projectId: string): string {
  return KEY_PREFIX + projectId;
}
function undoKey(projectId: string): string { return UNDO_PREFIX + projectId; }
function redoKey(projectId: string): string { return REDO_PREFIX + projectId; }

/** Push a snapshot of the current placements to a history stack (undo or
 *  redo). Each stack stores the *previous* state — undo reverts to it. */
function pushSnapshot(stackKey: string, current: ReadonlyArray<UserPlacement>): void {
  try {
    const raw = window.localStorage.getItem(stackKey);
    const stack = raw ? (JSON.parse(raw) as UserPlacement[][]) : [];
    stack.push([...current]);
    // Cap depth — 50 entries is plenty for designer-pace edits.
    if (stack.length > 50) stack.shift();
    window.localStorage.setItem(stackKey, JSON.stringify(stack));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[userPlacements] failed to push snapshot", err);
  }
}

function popSnapshot(stackKey: string): UserPlacement[] | null {
  try {
    const raw = window.localStorage.getItem(stackKey);
    if (!raw) return null;
    const stack = JSON.parse(raw) as UserPlacement[][];
    const top = stack.pop();
    if (!top) return null;
    window.localStorage.setItem(stackKey, JSON.stringify(stack));
    return top;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[userPlacements] failed to pop snapshot", err);
    return null;
  }
}

function clearStack(stackKey: string): void {
  try { window.localStorage.removeItem(stackKey); } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[userPlacements] failed to clear stack", err);
  }
}

export function readPlacements(projectId: string): UserPlacement[] {
  try {
    const raw = window.localStorage.getItem(key(projectId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((p): p is UserPlacement =>
      Boolean(p) && typeof p === "object"
      && typeof (p as UserPlacement).skuId === "string"
      && typeof (p as UserPlacement).roomId === "string",
    );
  } catch (err) {
    // CLAUDE.md §5 — surface, never swallow.
    // eslint-disable-next-line no-console
    console.error("[userPlacements] failed to read", err);
    return [];
  }
}

export function addPlacement(projectId: string, skuId: string, roomId: string): UserPlacement {
  const current = readPlacements(projectId);
  // Push current state onto undo BEFORE the mutation; clear redo (standard
  // undo/redo semantics — new mutation invalidates the redo history).
  pushSnapshot(undoKey(projectId), current);
  clearStack(redoKey(projectId));
  const placement: UserPlacement = {
    id: `upl-${projectId}-${skuId}-${roomId}-${Date.now().toString(36)}`,
    skuId,
    roomId,
    createdAt: new Date().toISOString(),
  };
  const next = [...current, placement];
  try {
    window.localStorage.setItem(key(projectId), JSON.stringify(next));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[userPlacements] failed to write", err);
  }
  return placement;
}

export function removePlacement(projectId: string, placementId: string): void {
  const next = readPlacements(projectId).filter((p) => p.id !== placementId);
  try {
    window.localStorage.setItem(key(projectId), JSON.stringify(next));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[userPlacements] failed to write", err);
  }
}

/** Remove the most recent user-added placement matching (skuId, roomId).
 *  Returns true when something was removed. Designer pressing X on a
 *  placement tile only sees user-added rows — sample/procgen IN_ROOM edges
 *  stay read-only. */
export function removePlacementByPair(projectId: string, skuId: string, roomId: string): boolean {
  const all = readPlacements(projectId);
  // Walk newest → oldest so the X removes the latest add first.
  const idx = [...all].reverse().findIndex((p) => p.skuId === skuId && p.roomId === roomId);
  if (idx < 0) return false;
  pushSnapshot(undoKey(projectId), all);
  clearStack(redoKey(projectId));
  const targetId = all[all.length - 1 - idx]!.id;
  const next = all.filter((p) => p.id !== targetId);
  try {
    window.localStorage.setItem(key(projectId), JSON.stringify(next));
    return true;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[userPlacements] failed to write", err);
    return false;
  }
}

/** Undo last mutation — restore the previous snapshot, push current to redo. */
export function undoPlacement(projectId: string): boolean {
  const prev = popSnapshot(undoKey(projectId));
  if (!prev) return false;
  const current = readPlacements(projectId);
  pushSnapshot(redoKey(projectId), current);
  try {
    window.localStorage.setItem(key(projectId), JSON.stringify(prev));
    return true;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[userPlacements] undo failed", err);
    return false;
  }
}

/** Redo a previously-undone mutation. */
export function redoPlacement(projectId: string): boolean {
  const next = popSnapshot(redoKey(projectId));
  if (!next) return false;
  const current = readPlacements(projectId);
  pushSnapshot(undoKey(projectId), current);
  try {
    window.localStorage.setItem(key(projectId), JSON.stringify(next));
    return true;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[userPlacements] redo failed", err);
    return false;
  }
}

/** Stack depth for the UI buttons' disabled state. */
export function undoDepth(projectId: string): number {
  try {
    const raw = window.localStorage.getItem(undoKey(projectId));
    if (!raw) return 0;
    const stack = JSON.parse(raw) as unknown[];
    return Array.isArray(stack) ? stack.length : 0;
  } catch { return 0; }
}
export function redoDepth(projectId: string): number {
  try {
    const raw = window.localStorage.getItem(redoKey(projectId));
    if (!raw) return 0;
    const stack = JSON.parse(raw) as unknown[];
    return Array.isArray(stack) ? stack.length : 0;
  } catch { return 0; }
}

/** True when (skuId, roomId) was added by the user (vs the sample graph). */
export function isUserAdded(projectId: string, skuId: string, roomId: string): boolean {
  return readPlacements(projectId).some((p) => p.skuId === skuId && p.roomId === roomId);
}

/** Merge user IN_ROOM placements into the project graph for rendering.
 *  Adds typed IN_ROOM edges only — does not duplicate or mutate the source.
 *  Also threads any per-(sku,room) position overrides onto the IN_ROOM
 *  edge's attrs so Scene3D + floorplanSvg can read them. */
export function mergeUserPlacements(graph: ProjectGraph, projectId: string): ProjectGraph {
  const placements = readPlacements(projectId);
  const positions = readPlacementPositions(projectId);
  if (placements.length === 0 && positions.size === 0) return graph;
  const skuIds = new Set(graph.nodes.filter((n) => n.kind === "SKU").map((n) => n.id));
  const roomIds = new Set(graph.nodes.filter((n) => n.kind === "Room").map((n) => n.id));
  const extra: GraphEdge[] = [];
  for (const p of placements) {
    if (!skuIds.has(p.skuId) || !roomIds.has(p.roomId)) continue;
    const pos = positions.get(positionKey(p.skuId, p.roomId));
    extra.push({
      id: p.id,
      kind: "IN_ROOM",
      source: p.skuId,
      target: p.roomId,
      weight: 1.0,
      attrs: {
        origin: "user",
        createdAt: p.createdAt,
        ...(pos ? { posX: pos.x, posZ: pos.z, posMovedAt: pos.movedAt } : {}),
      },
    });
  }
  // Also surface position overrides for SAMPLE-graph IN_ROOM edges
  // (the user can drag a seed placement too). We can't mutate the
  // source edge so we add a parallel "override" edge attrs-only?
  // Cleaner: thread the position into the SKU node's attrs as a
  // (room-keyed) override map the renderer reads.
  let nodes = graph.nodes;
  if (positions.size > 0) {
    nodes = graph.nodes.map((n) => {
      if (n.kind !== "SKU") return n;
      // Build per-room position attr keys — Scene3D / floorplanSvg
      // read `posX_${roomId}` to apply the override when placing.
      const overrides: Record<string, number | string> = {};
      let touched = false;
      for (const [k, v] of positions.entries()) {
        const [skuId, roomId] = k.split("|");
        if (skuId !== n.id || !roomId) continue;
        overrides[`posX_${roomId}`] = v.x;
        overrides[`posZ_${roomId}`] = v.z;
        overrides[`posMovedAt_${roomId}`] = v.movedAt;
        if (typeof v.yaw === "number" && Number.isFinite(v.yaw)) {
          overrides[`posYaw_${roomId}`] = v.yaw;
        }
        touched = true;
      }
      return touched ? { ...n, attrs: { ...n.attrs, ...overrides } } : n;
    });
  }
  if (extra.length === 0 && nodes === graph.nodes) return graph;
  return { ...graph, nodes, edges: [...graph.edges, ...extra] };
}

// ─── Position overrides ─────────────────────────────────────────────────

export interface PlacementPosition {
  /** x metres, relative to the room's origin (top-left corner). */
  readonly x: number;
  /** z metres, relative to the room's origin. */
  readonly z: number;
  /** Yaw rotation about Y axis, in radians. 0 = default category orientation;
   *  positive = counter-clockwise looking from above. Quantised by callers
   *  to 90° increments so wall-aligned placements snap cleanly. */
  readonly yaw?: number;
  readonly movedAt: string;
}

function positionStorageKey(projectId: string): string {
  return POSITION_KEY_PREFIX + projectId;
}
function positionKey(skuId: string, roomId: string): string {
  return `${skuId}|${roomId}`;
}

export function readPlacementPositions(projectId: string): Map<string, PlacementPosition> {
  try {
    const raw = window.localStorage.getItem(positionStorageKey(projectId));
    if (!raw) return new Map();
    const parsed = JSON.parse(raw) as Record<string, PlacementPosition>;
    return new Map(Object.entries(parsed));
  } catch (err) {
    console.error("[userPlacements] failed to read positions", err);
    return new Map();
  }
}

export function setPlacementPosition(projectId: string, skuId: string, roomId: string, x: number, z: number, yaw?: number): void {
  try {
    const map = readPlacementPositions(projectId);
    const prev = map.get(positionKey(skuId, roomId));
    const nextYaw = yaw !== undefined ? yaw : prev?.yaw;
    const entry: PlacementPosition = {
      x, z,
      movedAt: new Date().toISOString(),
      ...(nextYaw !== undefined ? { yaw: nextYaw } : {}),
    };
    map.set(positionKey(skuId, roomId), entry);
    window.localStorage.setItem(positionStorageKey(projectId), JSON.stringify(Object.fromEntries(map)));
  } catch (err) {
    console.error("[userPlacements] failed to persist position", err);
  }
}

/** Rotate a placement about its Y axis by `deltaRadians`. Reads the
 *  current yaw, adds the delta, and normalises into (-PI, PI]. Used by
 *  the keyboard / on-screen rotation control during drag. */
export function rotatePlacement(projectId: string, skuId: string, roomId: string, deltaRadians: number): number {
  const map = readPlacementPositions(projectId);
  const prev = map.get(positionKey(skuId, roomId));
  const nextYaw = ((prev?.yaw ?? 0) + deltaRadians + Math.PI * 3) % (Math.PI * 2) - Math.PI;
  if (prev) {
    map.set(positionKey(skuId, roomId), { ...prev, yaw: nextYaw, movedAt: new Date().toISOString() });
    try {
      window.localStorage.setItem(positionStorageKey(projectId), JSON.stringify(Object.fromEntries(map)));
    } catch (err) {
      console.error("[userPlacements] failed to persist yaw", err);
    }
  }
  return nextYaw;
}

export function clearPlacementPosition(projectId: string, skuId: string, roomId: string): void {
  try {
    const map = readPlacementPositions(projectId);
    map.delete(positionKey(skuId, roomId));
    window.localStorage.setItem(positionStorageKey(projectId), JSON.stringify(Object.fromEntries(map)));
  } catch (err) {
    console.error("[userPlacements] failed to clear position", err);
  }
}
